import type { Navigation, NavigationPageItem } from 'types/navigation'

export interface AssistantRouteTable {
  paths: ReadonlySet<string>
  aliases: Record<string, string>
  detailPatterns: RegExp[]
}

const SHARED_PATHS = [
  '/fleet-list',
  '/notification-center',
  '/account-settings',
  '/support-request',
]

/** Detail routes registered in AppRoutes beside the nav tree. */
const SHARED_DETAILS: RegExp[] = [
  /^\/admin\/financing-profiles\/client\/[^/]+$/,
  /^\/admin\/financing-profiles\/company\/[^/]+\/client\/[^/]+$/,
]

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function pathPattern(patternPath: string): RegExp {
  const body = patternPath
    .split('/')
    .filter(Boolean)
    .map(part => (part.startsWith(':') ? '[^/]+' : escapeRegExp(part)))
    .join('/')
  return new RegExp(`^/${body}$`)
}

function walk(items: Navigation, parentSegment: string | undefined, paths: Set<string>, aliases: Record<string, string>, details: RegExp[]) {
  for (const item of items) {
    if (item.kind === 'header' || item.kind === 'divider') continue
    const page = item as NavigationPageItem
    if (page.children && page.children.length > 0) {
      walk(page.children, page.segment, paths, aliases, details)
      continue
    }
    if (page.href && !page.component) continue
    const segment =
      parentSegment && page.segment ? `${parentSegment}/${page.segment}` : page.segment || ''
    if (!segment) continue
    const path = `/${segment}`
    paths.add(path)
    for (const slash of page.slashes ?? []) {
      if (!slash.startsWith('/') || slash === path) continue
      aliases[slash] = path
    }
    const babyRoutes = page.slugBabyRoutes?.length
      ? page.slugBabyRoutes
      : page.slugBabyPattern
        ? [{ pattern: page.slugBabyPattern }]
        : []
    for (const baby of babyRoutes) {
      details.push(pathPattern(`${path}/${baby.pattern}`))
      if (page.slugGrandBabyPattern && baby.pattern === page.slugBabyPattern) {
        details.push(pathPattern(`${path}/${baby.pattern}/${page.slugGrandBabyPattern}`))
      }
    }
  }
}

/** Paths and shortcuts taken from the same navigation the portal router renders. */
export function assistantRoutesFromNavigation(items: Navigation): AssistantRouteTable {
  const paths = new Set<string>(SHARED_PATHS)
  const aliases: Record<string, string> = {}
  const detailPatterns = [...SHARED_DETAILS]
  walk(items, undefined, paths, aliases, detailPatterns)
  return { paths, aliases, detailPatterns }
}
